'use client'

import dynamic from 'next/dynamic'
import { Component, useCallback, useEffect, useRef, useState } from 'react'
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent, ReactNode } from 'react'
import type { PresentationOrientation } from '@/components/product/PresentationScene'
import { renderTier, type RenderTier } from '@/components/product/studio/render-tier'
import styles from './PresentationStage.module.css'

/**
 * The hero presentation stage: the single integration surface for the MIST
 * birthstone set. The server, every no-JS visitor, every Save-Data or
 * WebGL-less browser, and the whole loading window all see the same authored
 * still — the complete hero, with no layout shift. Capable browsers upgrade in
 * place to the live 3D still-life, which is always in motion (short of a
 * reduced-motion preference) and opens — the lid swings, the gem cap floats
 * free, the bottle rises — when the visitor clicks the hero, closing again on
 * the next click.
 *
 * The stage owns every DOM concern — capability tiering, visibility gating,
 * pointer/keyboard turntable input, and the hero click — and hands the renderer
 * nothing but booleans and one mutable orientation ref. The canvas region is
 * decorative: everything it shows already exists as text in the surrounding
 * hero.
 *
 * The canvas exists only while the stage intersects a foregrounded viewport.
 * That is not only a frame budget: the homepage also runs the atelier bottle
 * canvas, and browsers cap live WebGL contexts per document, so the hero hands
 * its context back the moment the visitor scrolls past it.
 */

const PresentationScene = dynamic(() => import('@/components/product/PresentationScene'), { ssr: false })

/**
 * Authored still of the set: the plinth cut of the Blender poster
 * (scripts/export-presentation-plinth.mjs from
 * assets/images/product/mist-presentation.webp). Its backdrop is transparent
 * and its floor light fades out inside the frame, so the still stands in
 * whatever field the page gives it without a rectangle to hide.
 */
const POSTER = {
  src: '/media/product/mist-presentation-plinth.webp',
  width: 1600,
  height: 1200,
  alt: 'The MIST by BDQ January Garnet presentation: a faceted crystal bottle with a garnet gemstone cap, standing beside its open black and gold presentation case.',
} as const

/** Live-canvas readiness, mirrored to `data-scene` for deterministic checks. */
type SceneState = 'still' | 'loading' | 'ready' | 'fallback'

const KEY_TURN = 0.14
const KEY_TILT = 0.09
// The set is composed and lit from the front: the case's plain back and the
// bottle's unlabelled rear are not the product. Turning is an inspection arc,
// not a free orbit, so every angle the visitor can reach stays legible.
const YAW_LIMIT = 0.62
const PITCH_MIN = -0.3
const PITCH_MAX = 0.5
const DRAG_YAW_PER_PX = 0.0055
const DRAG_PITCH_PER_PX = 0.003
/** Pointer travel past which a press on the set was a turn, not a click. */
const CLICK_SLOP_PX = 6
/** Hero clicks on these keep their own job instead of opening the set. */
const INTERACTIVE = 'a, button, input, select, textarea, label, summary, [role="button"]'
// Generous on purpose: this budget only exists to stop a canvas that will never
// paint from sitting over the hero, and a cold cache on a slow connection (or a
// development server compiling the three.js graph for the first time) can take
// far longer than a page load.
const LOAD_BUDGET_MS = 30000
/** Fresh canvases granted after a lost context before the still wins. */
const MAX_CONTEXT_ATTEMPTS = 2
/**
 * CPU rasterisers (headless CI, VMs without GPU passthrough, machines that fell
 * back from a blocklisted driver) report themselves here. They can create a
 * context and then need tens of seconds to compile a PBR shader, so the live
 * scene would sit blank over the hero and eventually lose the context: the
 * authored still is both faster and better on those machines.
 */
const SOFTWARE_RENDERER = /swiftshader|llvmpipe|softpipe|software rasterizer|microsoft basic render/i

type Props = {
  className?: string
}

type DragState = { pointerId: number; x: number; y: number; pointerType: string; travel: number }

/**
 * The GLB and the WebGL context are the two things that can fail after the
 * upgrade decision. Either one throwing must restore the authored still rather
 * than blank the hero, so the live scene renders behind a boundary that reports
 * the failure and unmounts itself.
 */
class SceneBoundary extends Component<{ onFailure: (reason: string) => void; children: ReactNode }, { failed: boolean }> {
  override state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  override componentDidCatch(error: Error) {
    this.props.onFailure(`render:${error.message.slice(0, 120)}`)
  }

  override render() {
    return this.state.failed ? null : this.props.children
  }
}

export default function PresentationStage({ className }: Props) {
  const stageRef = useRef<HTMLDivElement>(null)
  const orientationRef = useRef<PresentationOrientation>({
    targetYaw: 0,
    targetPitch: 0,
    pointerX: 0,
    pointerY: 0,
  })
  const requestFrameRef = useRef<(() => void) | null>(null)
  const dragRef = useRef<DragState | null>(null)
  // Travel of the last press on the set, kept past pointerup for the click.
  const lastTravelRef = useRef(0)
  // Canvas generation: bumping it hands the scene a brand new canvas element.
  const attemptRef = useRef(0)

  const [enhanced, setEnhanced] = useState(false)
  const [scene, setScene] = useState<SceneState>('still')
  const [motionOk, setMotionOk] = useState(true)
  const [revealed, setRevealed] = useState(false)
  const [inView, setInView] = useState(false)
  const [documentVisible, setDocumentVisible] = useState(true)
  const [attempt, setAttempt] = useState(0)
  // The render tier (studio or lite), decided once with the collection card
  // and reported as `data-tier` so verifiers can tell which family painted.
  const [tier, setTier] = useState<RenderTier | null>(null)

  // Capability tier, decided once. The live scene costs a WebGL context, a GLB,
  // and a PBR shader compile, so Save-Data, context-less browsers, and CPU
  // rasterisers keep the authored still.
  useEffect(() => {
    let usable = false
    try {
      const probe = document.createElement('canvas')
      const context = probe.getContext('webgl2') ?? probe.getContext('webgl')
      if (context) {
        const info = context.getExtension('WEBGL_debug_renderer_info')
        const renderer = info === null ? '' : String(context.getParameter(info.UNMASKED_RENDERER_WEBGL))
        usable = !SOFTWARE_RENDERER.test(renderer)
      }
      // The probe must not keep a context alive: contexts are a scarce
      // per-document resource and this page runs a second canvas of its own.
      context?.getExtension('WEBGL_lose_context')?.loseContext()
    } catch {
      usable = false
    }
    const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection
    if (!usable || connection?.saveData === true) return

    setTier(renderTier())
    setEnhanced(true)
    setScene('loading')
    const query = window.matchMedia('(prefers-reduced-motion: reduce)')
    const applyMotion = () => setMotionOk(!query.matches)
    applyMotion()
    query.addEventListener('change', applyMotion)
    return () => query.removeEventListener('change', applyMotion)
  }, [])

  // Off-screen and background tabs must not pay for a frame — and must not hold
  // a WebGL context either: the page has a second live canvas further down, and
  // some browsers drop the oldest context when a second one is created.
  useEffect(() => {
    if (!enhanced) return
    const target = stageRef.current
    if (!target) return
    const observer = new IntersectionObserver(([entry]) => setInView(Boolean(entry?.isIntersecting)), {
      rootMargin: '120px',
    })
    observer.observe(target)
    const onVisibility = () => setDocumentVisible(document.visibilityState === 'visible')
    onVisibility()
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      observer.disconnect()
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [enhanced])

  /**
   * A lost context is recoverable and a dead canvas is not: a canvas whose
   * context was lost — including one force-lost by R3F's own teardown, which is
   * what React's development double-effect provokes — never gets it back, so the
   * fix is a brand new canvas element. A bounded number of fresh canvases is
   * granted, and the stage does not change its reported state while it retries:
   * dropping back to `loading` would blink the controls and the poster in and
   * out for the few hundred milliseconds the replacement takes. Every other
   * failure (a broken export, a render throw, a load that never lands) is not
   * transient and goes straight to the still.
   */
  const onFailure = useCallback((reason: string) => {
    if (reason === 'context-lost' && attemptRef.current < MAX_CONTEXT_ATTEMPTS) {
      attemptRef.current += 1
      setAttempt(attemptRef.current)
      return
    }
    setScene((current) => (current === 'fallback' ? current : 'fallback'))
    if (process.env.NODE_ENV !== 'production') console.warn(`[PresentationStage] live scene unavailable: ${reason}`)
  }, [])

  const onReady = useCallback(() => setScene((current) => (current === 'fallback' ? current : 'ready')), [])

  const live = enhanced && scene !== 'fallback'
  // The context only exists while the hero is actually on screen in a
  // foregrounded tab; leaving the viewport releases it back to the browser.
  const mounted = live && inView && documentVisible
  // Controls only exist once the canvas has actually painted: nothing on this
  // stage is interactive while the poster is the only thing on screen.
  const ready = live && scene === 'ready'
  const ambient = mounted && ready && motionOk

  // A never-resolving asset would otherwise hold a blank canvas over the hero.
  useEffect(() => {
    if (scene !== 'loading' || !mounted) return
    const timer = window.setTimeout(() => onFailure('load-timeout'), LOAD_BUDGET_MS)
    return () => window.clearTimeout(timer)
  }, [scene, mounted, onFailure])

  // Leaving the viewport tears the canvas down, so readiness must go with it:
  // the next mount has to paint a frame before the stage calls itself live
  // again, otherwise the controls would sit over a blank canvas for a beat.
  useEffect(() => {
    if (mounted || scene !== 'ready') return
    setScene('loading')
  }, [mounted, scene])

  // A click anywhere in the hero opens or closes the set, once it is live. The
  // hero's own links and buttons keep their job, and a press that turned the
  // set is a drag, not a click. Every press in the hero clears the last travel
  // first (this native listener runs before React's root-delegated handlers).
  useEffect(() => {
    if (!ready) return
    const stage = stageRef.current
    const hero = stage?.closest('section') ?? stage
    if (!hero) return
    const onPress = () => {
      lastTravelRef.current = 0
    }
    const onClick = (event: MouseEvent) => {
      if (event.target instanceof Element && event.target.closest(INTERACTIVE)) return
      if (lastTravelRef.current > CLICK_SLOP_PX) return
      setRevealed((current) => !current)
    }
    hero.addEventListener('pointerdown', onPress)
    hero.addEventListener('click', onClick)
    return () => {
      hero.removeEventListener('pointerdown', onPress)
      hero.removeEventListener('click', onClick)
    }
  }, [ready])

  const onPointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (!event.isPrimary) return
    dragRef.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      pointerType: event.pointerType,
      travel: 0,
    }
    event.currentTarget.setPointerCapture(event.pointerId)
  }, [])

  const onPointerMove = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const orientation = orientationRef.current
    const drag = dragRef.current
    if (drag && drag.pointerId === event.pointerId) {
      const turned = orientation.targetYaw + (event.clientX - drag.x) * DRAG_YAW_PER_PX
      orientation.targetYaw = Math.min(Math.max(turned, -YAW_LIMIT), YAW_LIMIT)
      // Touch keeps its vertical axis for scrolling (`touch-action: pan-y`), so
      // only mouse and pen drags tilt the view.
      if (drag.pointerType !== 'touch') {
        const next = orientation.targetPitch + (event.clientY - drag.y) * DRAG_PITCH_PER_PX
        orientation.targetPitch = Math.min(Math.max(next, PITCH_MIN), PITCH_MAX)
      }
      drag.travel += Math.abs(event.clientX - drag.x) + Math.abs(event.clientY - drag.y)
      drag.x = event.clientX
      drag.y = event.clientY
    } else if (event.pointerType === 'mouse') {
      const rect = event.currentTarget.getBoundingClientRect()
      orientation.pointerX = ((event.clientX - rect.left) / Math.max(rect.width, 1)) * 2 - 1
      orientation.pointerY = 1 - ((event.clientY - rect.top) / Math.max(rect.height, 1)) * 2
    }
    requestFrameRef.current?.()
  }, [])

  const endDrag = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return
    lastTravelRef.current = drag.travel
    dragRef.current = null
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
  }, [])

  const onPointerLeave = useCallback(() => {
    orientationRef.current.pointerX = 0
    orientationRef.current.pointerY = 0
    requestFrameRef.current?.()
  }, [])

  // The keyboard equivalent of the hero click and the drag turntable.
  const onKeyDown = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
    const orientation = orientationRef.current
    switch (event.key) {
      case 'Enter':
      case ' ':
        // A held key would otherwise flap the lid on every auto-repeat.
        if (!event.repeat) setRevealed((current) => !current)
        break
      case 'ArrowLeft':
        orientation.targetYaw = Math.max(orientation.targetYaw - KEY_TURN, -YAW_LIMIT)
        break
      case 'ArrowRight':
        orientation.targetYaw = Math.min(orientation.targetYaw + KEY_TURN, YAW_LIMIT)
        break
      case 'ArrowUp':
        orientation.targetPitch = Math.min(orientation.targetPitch + KEY_TILT, PITCH_MAX)
        break
      case 'ArrowDown':
        orientation.targetPitch = Math.max(orientation.targetPitch - KEY_TILT, PITCH_MIN)
        break
      case 'Home':
        orientation.targetYaw = 0
        orientation.targetPitch = 0
        break
      default:
        return
    }
    event.preventDefault()
    requestFrameRef.current?.()
  }, [])

  // The canvas is decorative and aria-hidden; the surrounding hero copy carries
  // the product story. A permanent live region here would only add a second
  // status announcement to the page.
  return (
    <div
      ref={stageRef}
      className={className ? `${styles.stage} ${className}` : styles.stage}
      data-scene={scene}
      data-tier={tier ?? undefined}
      data-reveal={revealed ? 'open' : 'closed'}
      data-motion={live && motionOk ? 'running' : 'off'}
    >
      <img
        className={styles.poster}
        src={POSTER.src}
        alt={POSTER.alt}
        width={POSTER.width}
        height={POSTER.height}
        // The still is the hero on first paint and on every fallback path, so it
        // is never lazy and never decoded late.
        decoding="sync"
        fetchPriority="high"
      />

      {mounted ? (
        <div
          className={styles.surface}
          data-visible={ready ? 'true' : undefined}
          // Until the first frame has painted there is nothing live to turn, so
          // the surface is neither focusable nor announced.
          role={ready ? 'img' : undefined}
          aria-hidden={ready ? undefined : true}
          aria-label={
            ready
              ? `Live view of the MIST January Garnet presentation set. Press Enter to ${revealed ? 'close' : 'open'} it. Drag, or use the arrow keys, to turn it.`
              : undefined
          }
          tabIndex={ready ? 0 : -1}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onLostPointerCapture={endDrag}
          onPointerLeave={onPointerLeave}
          onKeyDown={onKeyDown}
        >
          <div className={styles.canvas} aria-hidden="true">
            <SceneBoundary key={attempt} onFailure={onFailure}>
              <PresentationScene
                revealed={revealed}
                ambient={ambient}
                motionOk={motionOk}
                orientationRef={orientationRef}
                requestFrameRef={requestFrameRef}
                tier={tier ?? 'lite'}
                onReady={onReady}
                onFailure={onFailure}
              />
            </SceneBoundary>
          </div>
        </div>
      ) : null}

      {mounted && ready ? (
        <div className={styles.controls}>
          <p className={styles.hint} aria-hidden="true">
            {revealed ? 'Click to close — drag to turn' : 'Click to open — drag to turn'}
          </p>
        </div>
      ) : null}
    </div>
  )
}
